import axiosInstance from './apiClient' // Import centralized axios instance
import { AxiosError } from 'axios'
import { User } from '../redux/types/user'
import { getErrorMessage } from '@/lib/utils'
import {
  getAccessToken,
  removeAccessToken,
  setAccessToken,
  setAuthTokens,
} from '@/utils/authUtils'
import { UserRole } from '@/constants/room'

export const registerUser = async (data: {
  name: string
  email: string
  password1: string
  password2: string
  role: string
}) => {
  try {
    const response = await axiosInstance.post(
      '/auth/dj-rest-auth/registration/',
      data
    )
    return response.data
  } catch (error) {
    if (error instanceof AxiosError && error.response?.data) {
      throw new Error(
        error.response.data.message || JSON.stringify(error.response.data)
      )
    }
    throw new Error(getErrorMessage(error))
  }
}

export const changePassword = async (data: {
  new_password1: string
  new_password2: string
}) => {
  try {
    const response = await axiosInstance.post(
      '/auth/dj-rest-auth/password/change/',
      data
    )
    return response.data
  } catch (error) {
    if (error instanceof AxiosError && error.response?.data) {
      throw new Error(
        error.response.data.message || JSON.stringify(error.response.data)
      )
    }
    throw new Error(getErrorMessage(error))
  }
}

export const verifyEmailKey = async (data: { key: string }) => {
  try {
    const response = await axiosInstance.post(
      '/auth/dj-rest-auth/registration/verify-email/',
      data
    )
    return response.data
  } catch (error) {
    if (error instanceof AxiosError && error.response?.data) {
      throw new Error(
        error.response.data.message || JSON.stringify(error.response.data)
      )
    }
    throw new Error(getErrorMessage(error))
  }
}

export const loginUser = async (data: { email: string; password: string }) => {
  try {
    removeAccessToken()

    const response = await axiosInstance.post('/auth/dj-rest-auth/login/', data)

    if (response.data?.token) {
      setAccessToken(response.data.token)
    }

    return response.data
  } catch (error) {
    if (error instanceof AxiosError && error.response?.data) {
      const errorMessage =
        error.response.data.detail ||
        (typeof error.response.data === 'object'
          ? JSON.stringify(error.response.data)
          : 'Login failed')
      throw new Error(errorMessage)
    }
    throw new Error(getErrorMessage(error))
  }
}

export const fetchUserDataFromAPI = async (): Promise<User | null> => {
  const token = getAccessToken()
  if (!token) {
    return null
  }
  try {
    const response = await axiosInstance.get<User>('/auth/dj-rest-auth/user/', {
      headers: {
        Accept: 'application/json',
      },
    })
    return response.data
  } catch (error) {
    if (error instanceof AxiosError && error.response?.data) {
      throw new Error(
        error.response.data.message || JSON.stringify(error.response.data)
      )
    }
    throw new Error(getErrorMessage(error))
  }
}

export const logoutUser = async () => {
  try {
    const response = await axiosInstance.post('/auth/dj-rest-auth/logout/')
    return response.data
  } catch (error) {
    if (error instanceof AxiosError && error.response?.data) {
      throw new Error(
        error.response.data.message || JSON.stringify(error.response.data)
      )
    }
    throw new Error(getErrorMessage(error))
  }
}

export const forgotPasswordUser = async (data: { email: string }) => {
  try {
    const response = await axiosInstance.post('/auth/forgot-password/', data)
    return response.data
  } catch (error) {
    throw new Error(getErrorMessage(error))
  }
}

export const verifyCodeUser = async (data: {
  otp: string
  temp_token: string
}) => {
  try {
    const response = await axiosInstance.post('/auth/verify-otp/', data)
    return response.data
  } catch (error) {
    throw new Error(getErrorMessage(error))
  }
}

export const confirmResetPasswordUser = async (data: {
  new_password1: string
  new_password2: string
  uid: string
  token: string
}) => {
  try {
    const response = await axiosInstance.post(
      '/auth/dj-rest-auth/password/reset/confirm/',
      data
    )
    return response.data
  } catch (error) {
    throw new Error(getErrorMessage(error))
  }
}

export const setup2FA = async (data: {
  temp_token: string
  skip_2fa: boolean
}) => {
  try {
    const response = await axiosInstance.post('/auth/setup-2fa/', data)
    return response.data
  } catch (error) {
    throw new Error(getErrorMessage(error))
  }
}

export const updateUserProfile = async (formData: FormData) => {
  try {
    const response = await axiosInstance.put(
      '/auth/update-profile/',
      formData,
      {
        headers: { 'Content-Type': 'multipart/form-data' },
      }
    )
    return response.data
  } catch (error) {
    throw new Error(getErrorMessage(error))
  }
}

export const createGuestLink = async (data: {
  role: UserRole
  room_id?: string
  max_uses?: number
  expires_in_minutes?: number
}) => {
  const response = await axiosInstance.post('/auth/guest-links/', data)
  return response.data as {
    token: string
    role: string
    room: string | null
    expires_at: string | null
    is_active: boolean
    max_uses: number
    uses: number
  }
}

export const consumeGuestLink = async (token: string, displayName?: string) => {
  const response = await axiosInstance.post('/auth/guest-links/consume/', {
    token,
    display_name: displayName ?? '',
  })
  const { access, refresh, user, room_id, invite_code } = response.data
  if (access && refresh) {
    setAuthTokens(access, refresh)
  }
  return { user, room_id, invite_code }
}
